import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang, UI } from "@/i18n";
import { useContent } from "@/content";
import { Wordmark, Starfield, Star } from "@/components/Monogram";
import { MaskedLine, Eyebrow } from "@/components/Reveal";
import { waHref } from "@/components/Nav";

export default function Hero() {
  const { lang, t } = useLang();
  const { content } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const hero = (content && content.hero) || null;
  const contacts = (content && content.contacts) || { whatsapp: "393751163111" };

  return (
    <section ref={ref} className="relative flex min-h-[100svh] flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: videoY }} className="absolute inset-0 scale-[1.12]">
        {hero && (
          <video
            key={hero.video}
            className="img-spotlight h-full w-full object-cover"
            src={hero.video}
            poster={hero.poster}
            autoPlay
            muted
            loop
            playsInline
            data-testid="hero-video"
          />
        )}
      </motion.div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(10,10,12,0.55) 0%, rgba(10,10,12,0.35) 45%, rgba(10,10,12,0.92) 100%)",
        }}
      />
      <Starfield className="z-10" />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col items-center justify-center px-6 pt-28 pb-20 text-center sm:px-10"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <Wordmark size="lg" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.9 }}
          className="mt-10 flex items-center gap-3"
          data-testid="hero-eyebrow"
        >
          <Star size={8} fill="#C5A059" />
          <span className="eyebrow !text-[#FAF9F6]/80" style={{ fontSize: "12px" }}>
            {(UI.hero.headline[lang] || UI.hero.headline.it).join(" ")}
          </span>
          <Star size={8} fill="#C5A059" />
        </motion.div>

        <h1 className="font-display mt-6 text-[clamp(2.6rem,7vw,5.4rem)] font-light uppercase leading-[1.06] text-[#FAF9F6]" data-testid="hero-headline">
          {["Move privately.", "Move exclusively."].map((line, i) => (
            <MaskedLine key={i} delay={1 + i * 0.18}>
              {line}
            </MaskedLine>
          ))}
        </h1>

        {hero && (
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.9 }}
            className="mt-7 text-[14px] font-light tracking-[0.28em] text-[#C5A059] sm:text-[15px]"
            style={{ fontFamily: "var(--font-sans)", textShadow: "0 1px 12px rgba(10,10,12,0.8)" }}
            data-testid="hero-payoff"
          >
            {t(hero.payoff)}
          </motion.p>
        )}

        {hero && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 0.9 }}
            className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
          >
            <a
              href={waHref(contacts, lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold-bright"
              data-testid="hero-cta-whatsapp"
            >
              {t(hero.cta_primary)}
            </a>
            <a
              href={waHref(contacts, lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-white"
              data-testid="hero-cta-phone"
            >
              +39 375 116 3111 · Nicky
            </a>
          </motion.div>
        )}

        {hero && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.3, duration: 1 }}
            className="mt-8 max-w-lg text-[13px] font-light leading-relaxed text-[#FAF9F6]/55 sm:text-sm"
            style={{ textShadow: "0 1px 10px rgba(10,10,12,0.7)" }}
          >
            {t(hero.microcopy)}
          </motion.p>
        )}
      </motion.div>

      <div className="relative z-10 flex justify-center pb-8">
        <div className="flex flex-col items-center gap-3">
          <div className="h-14 w-px overflow-hidden">
            <div className="scroll-cue-line h-full w-px bg-[#C5A059]" />
          </div>
        </div>
      </div>
    </section>
  );
}
